/**
 * The two site-wide marketing buttons (build F10) and the shared trust line.
 *
 *  - Primary:   "Start free" → /signup
 *  - Secondary: "Watch the welcome video" → the existing welcome video
 *
 * Both fire `cta_clicked { cta_id, location }`.
 */
import { Link } from "wouter";
import { ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { track } from "@/lib/marketing/analytics";
import { TOUR_VIDEO_URL, signupHref } from "@/lib/marketing/links";
import { TRUST_LINE } from "@/lib/marketing/pricing-copy";

export const START_FREE_LABEL = "Start free";
export const TOUR_LABEL = "Watch the welcome video";

interface CtaProps {
  /** Stable id for analytics, e.g. "hero_start_free". */
  ctaId: string;
  /** Where the button sits, e.g. "home_hero", "header". */
  location: string;
  className?: string;
  size?: "default" | "sm" | "lg";
  /** Extra signup query params, e.g. { role: "property_manager" }. */
  signupParams?: Record<string, string>;
  /** Overrides the label. Only the pricing cards and /syre may do this (F10 exceptions). */
  label?: string;
  showArrow?: boolean;
  onClick?: () => void;
}

export function StartFreeButton({
  ctaId,
  location,
  className,
  size = "lg",
  signupParams,
  label = START_FREE_LABEL,
  showArrow = true,
  onClick,
}: CtaProps) {
  return (
    <Button
      asChild
      size={size}
      className={cn(
        "bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold shadow-lg shadow-orange-500/20",
        className,
      )}
      data-testid={`button-${ctaId}`}
    >
      <Link
        href={signupHref(signupParams)}
        onClick={() => {
          track("cta_clicked", { cta_id: ctaId, location });
          onClick?.();
        }}
      >
        {label}
        {showArrow && <ArrowRight size={18} className="ml-1" />}
      </Link>
    </Button>
  );
}

export function TourButton({
  ctaId,
  location,
  className,
  size = "lg",
  tone = "dark",
}: Omit<CtaProps, "signupParams" | "label" | "showArrow" | "onClick"> & { tone?: "dark" | "light" }) {
  return (
    <Button
      asChild
      size={size}
      variant="outline"
      className={cn(
        tone === "dark"
          ? "border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
          : "border-gray-300 text-gray-800 hover:bg-gray-50",
        className,
      )}
      data-testid={`button-${ctaId}`}
    >
      <a
        href={TOUR_VIDEO_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("cta_clicked", { cta_id: ctaId, location })}
      >
        <PlayCircle size={18} className="mr-1" />
        {TOUR_LABEL}
      </a>
    </Button>
  );
}

/** A2: "$49 a property · 14-day free trial, no card · Cancel anytime". */
export function TrustLine({ className, tone = "dark" }: { className?: string; tone?: "dark" | "light" }) {
  return (
    <p
      className={cn("text-sm", tone === "dark" ? "text-blue-100/80" : "text-gray-500", className)}
      data-testid="text-trust-line"
    >
      {TRUST_LINE}
    </p>
  );
}
